import { getAuthToken } from "../../utils/authHelpers";

export const getViewerEmail = () => {
  if (typeof window === "undefined") return "";
  try { return (localStorage.getItem("postaway_email") || "").trim().toLowerCase(); } catch (_) { return ""; }
};

const authHeaders = () => {
  const token = getAuthToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  // Same trust level as the rest of the app: the API prefers the JWT and
  // only falls back to this when there is no token.
  const email = getViewerEmail();
  if (email) headers["X-Viewer-Email"] = email;
  return headers;
};

const getJson = async (url) => {
  const res = await fetch(url, { headers: authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.ok) throw new Error(data.error || `request_failed_${res.status}`);
  return data;
};

const postJson = async (url, body) => {
  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.ok) {
    const err = new Error(data.error || `request_failed_${res.status}`);
    err.data = data;
    throw err;
  }
  return data;
};

export const fetchCourses = () => getJson("/api/learning/courses");
export const fetchCourse = (slug) => getJson(`/api/learning/course?slug=${encodeURIComponent(slug)}`);
export const fetchAccess = () => postJson("/api/learning/access", { email: getViewerEmail() });
export const saveProgress = (lessonId, secondsWatched, completed) =>
  postJson("/api/learning/progress", { lessonId, secondsWatched, completed });
export const toggleSave = (courseId) => postJson("/api/learning/save", { courseId });
export const saveNote = (lessonId, body) => postJson("/api/learning/notes", { lessonId, body });

// Postaway Academy (coming soon / packages)
export const joinWaitlist = (payload) => postJson("/api/learning/waitlist", payload);
export const applyToContribute = (payload) => postJson("/api/learning/contribute", payload);
export const claimAccess = (email) => postJson("/api/learning/claim", { email });
export const createPackageCheckout = (payload) => postJson("/api/learning/checkout", payload);
export const verifyPackageCheckout = (sessionId) =>
  getJson(`/api/learning/checkout?session_id=${encodeURIComponent(sessionId)}`);

/** 49900 -> "$499", 69950 -> "$699.50" */
export const formatPrice = (cents) => {
  const n = Number(cents) || 0;
  return `$${(n / 100).toLocaleString("en-US", {
    minimumFractionDigits: n % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

/** 3725 -> "1h 2m", 540 -> "9m", 45 -> "0m" */
export const formatDuration = (seconds) => {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  return `${m}m`;
};

/** 125 -> "2:05" */
export const formatClock = (seconds) => {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
};

export const FORMAT_LABEL = {
  course: "Course",
  playlist: "Playlist",
  series: "Series",
  guide: "Guide",
};

/** Label for the lesson-count chip, e.g. "6 lessons · 1h 12m" */
export const countLabel = (course) => {
  if (!course) return "";
  if (course.format === "guide") return "PDF guide";
  const n = Number(course.lesson_count) || 0;
  const noun = course.format === "series" ? "episode" : course.format === "playlist" ? "track" : "lesson";
  if (n === 0 && course.access === "package") return "Sessions coming soon";
  const count = `${n} ${noun}${n === 1 ? "" : "s"}`;
  return n > 0 && course.total_seconds > 0 ? `${count} · ${formatDuration(course.total_seconds)}` : count;
};

export const progressPct = (course) => {
  if (!course || !course.lesson_count) return 0;
  return Math.round((Number(course.completed_count) || 0) / Number(course.lesson_count) * 100);
};

/** What this viewer pays for a package (2026 attendees get the discounted rate). */
export const priceFor = (pkg, isAttendee = false) =>
  isAttendee && pkg?.attendee_price_cents != null ? pkg.attendee_price_cents : pkg?.price_cents;

/** Cheapest package that unlocks a course, for the card price tag. */
export const cheapestPackageFor = (course, packages = [], isAttendee = false) => {
  const slugs = course?.package_slugs || [];
  const options = packages.filter((p) => slugs.includes(p.slug));
  if (!options.length) return null;
  return options.slice().sort((a, b) => priceFor(a, isAttendee) - priceFor(b, isAttendee))[0];
};
